/**
 * useLoadingAppData Hook
 * 
 * Central hook for loading all app data on startup:
 * 1. Restore auth state (verify session) - runs once on mount
 * 2. Load app data when authenticated - runs when isAuthenticated changes
 */

import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { AppDispatch, RootState } from '../store';
import { setAuth } from '../store/slices/authSlice';
import { setReports } from '../store/slices/annualReportSlice';
import { setGlobalPageSize } from '../store/slices/generalSlice';
import { getCurrentUser } from '../services/authService';
import { getAnnualReports } from '../services/financialStatementService';
import { getTokenInfo, getGlobalPageSize } from '../utils/storageUtils';
import { getLikelihoodRubricItems, getSeverityRubricItems, parseRubricItems } from '@/services/riskAssessmentService';
import { setLikelihoodRubricItems } from '@/store/slices/riskAssessmentSlice';
import { setSeverityRubricItems } from '@/store/slices/riskAssessmentSlice';

export const useLoadingAppData = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const annualReportId = selectedReport?.annualReportId;
  const [sessionLoading, setSessionLoading] = useState(true);
  const initialized = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode (development only)
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    // Restore global page size from storage
    const savedPageSize = getGlobalPageSize();
    if (savedPageSize) {
      dispatch(setGlobalPageSize(savedPageSize));
    }

    const restoreAuthState = async () => {
      try {
        // Check if auth tokens exist
        const authInfo = getTokenInfo();
        if (!authInfo || !authInfo.accessToken) {
          // console.log('ℹ️  No auth token found, skipping session restore');
          setSessionLoading(false);
          return;
        }

        // Verify token with backend by getting current user
        // console.log('🔐 Restoring auth state...');
        const userResponse = await getCurrentUser();
        
        // Update Redux state with user from backend
        dispatch(setAuth({ user: userResponse.data }));
        // console.log('✅ Auth state restored successfully');
      } catch (error: any) {
        // Auth restoration failed - tokens might be invalid
        // Let the automatic logout mechanism handle cleanup
        console.error('❌ Auth state restoration failed:', error);
        // Don't show error message - automatic logout will handle it
      } finally {
        setSessionLoading(false);
      }
    };

    restoreAuthState();
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadAppData = async () => {
      // console.log('📊 Loading app data...');

      // Load annual reports
      try {
        const annualReports = await getAnnualReports();
        dispatch(setReports(annualReports));
        // console.log('✅ Annual reports loaded successfully');
      } catch (error: any) {
        console.error('❌ Failed to load annual reports:', error);
        message.error(error?.message || 'Failed to load annual reports');
      }
      
    };

    loadAppData();
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!annualReportId) return;

    const fetchRubricItems = async () => {
      try {
        // console.log('🔄 Fetching rubric items...');

        // Fetch both in parallel
        const [likelihoodResponse, severityResponse] = await Promise.all([
          getLikelihoodRubricItems(annualReportId, { skip: 0, take: 5 }),
          getSeverityRubricItems(annualReportId, { skip: 0, take: 5 }),
        ]);

        const parsedLikelihood = parseRubricItems(likelihoodResponse);
        const parsedSeverity = parseRubricItems(severityResponse);

        // Dispatch both to Redux at once
        dispatch(setLikelihoodRubricItems(parsedLikelihood));
        dispatch(setSeverityRubricItems(parsedSeverity));

        // console.log(`✅ Rubric items loaded: ${parsedLikelihood.length} likelihood, ${parsedSeverity.length} severity`);
      } catch (error: any) {
        console.error('❌ Failed to fetch rubric items:', error);
        message.error(error?.message || 'Failed to load rubric items');
      }
    };

    fetchRubricItems();
  }, [annualReportId, dispatch]);

  return { sessionLoading };
};
